// src/pages/esp/AddReadingQuestionsToEsp.jsx
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BookText,
  Save,
  ArrowRight,
  Loader2,
  Image as ImageIcon,
  X,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { espQuestionsAPI } from "../../services/espService";
import TranslationField from "../../components/TranslationField";

/* ------------------------------------------------------------------ */
/* Reusable rich text editor (Bold / Italic / Underline / Highlight)   */
/* Used for: question text, options, explanation (AR), explanation (EN)*/
/* ------------------------------------------------------------------ */
function ToolbarBtn({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      // prevent the editor from losing focus/selection before the command runs
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-amber-100 text-amber-700"
          : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Generic rich text editor.
 *
 * @param {string} value        current HTML value
 * @param {(html:string)=>void} onChange
 * @param {"rtl"|"ltr"} dir      text direction
 * @param {string} minHeight    tailwind min-h-[...] class value, e.g. "60px"
 * @param {boolean} compact     smaller toolbar padding, useful inside option rows
 */
function RichTextEditor({
  value,
  onChange,
  dir = "ltr",
  minHeight = "90px",
  compact = false,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none min-h-[${minHeight}] px-3 ${
          compact ? "py-1.5" : "py-2"
        } focus:outline-none ${dir === "rtl" ? "text-right" : "text-left"}`,
        dir,
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-amber-500 bg-white">
      <div
        className={`flex gap-1 border-b border-gray-200 bg-gray-50 rounded-t-xl ${
          compact ? "p-0.5" : "p-1"
        }`}
      >
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Highlight"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/** Strips HTML tags to check whether an editor's content is effectively empty */
const isHtmlEmpty = (html) => !html || html.replace(/<[^>]*>/g, "").trim() === "";

/* ------------------------------------------------------------------ */

export default function AddReadingQuestionsToEsp() {
  const navigate = useNavigate();
  const { skillId, passageId } = useParams();

  const emptyQ = {
    question_text: "",
    options: ["", "", "", ""],
    // instead of storing the correct answer's text (which breaks once
    // options are rich HTML), we store the index of the correct option
    correctIndex: null,
    explanation: "",
    english_explanation: "",
    translation: "",
    difficulty: "MEDIUM",
    points: 1,
  };

  const [form, setForm] = useState(emptyQ);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Tiptap editors only read `content` on mount, not on every value change,
  // so changing `form` alone won't visually clear them after a save.
  // Bumping this key forces all RichTextEditor instances to remount fresh.
  const [formKey, setFormKey] = useState(0);

  const updateOption = (i, html) => {
    const opts = [...form.options];
    opts[i] = html;
    setForm({ ...form, options: opts });
  };

  const handleQuestionTextChange = (html) => {
    setForm((prev) => ({ ...prev, question_text: html }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isHtmlEmpty(form.question_text)) {
      setError("نص السؤال مطلوب");
      return;
    }
    if (
      form.correctIndex === null ||
      isHtmlEmpty(form.options[form.correctIndex])
    ) {
      setError("الإجابة الصحيحة مطلوبة");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const filledOptions = form.options.filter((o) => !isHtmlEmpty(o));

      const formData = new FormData();
      formData.append("question_text", form.question_text);
      formData.append("options", JSON.stringify(filledOptions));
      // send the correct option's HTML content as before, backend still
      // receives a string it can compare / store
      formData.append("correct_answer", form.options[form.correctIndex]);
      formData.append("explanation", form.explanation);
      formData.append("english_explanation", form.english_explanation);
      formData.append("translation", form.translation || "");
      formData.append("difficulty", form.difficulty);
      formData.append("points", form.points);
      if (imageFile) {
        formData.append("question_image", imageFile);
      }

      await espQuestionsAPI.createReadingQuestion(passageId, formData);
      setSuccess("تم إضافة السؤال!");
      setForm(emptyQ);
      setImageFile(null);
      setImagePreview(null);
      setFormKey((k) => k + 1);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/dashboard/esp/skills/${skillId}`)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
          <BookText className="w-5 h-5 text-amber-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">إضافة سؤال للقطعة</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm">
            {success}
          </div>
        )}

        {/* Question Text */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            نص السؤال <span className="text-red-500">*</span>
          </label>
          <RichTextEditor
            key={`qt-${formKey}`}
            value={form.question_text}
            onChange={handleQuestionTextChange}
            dir="ltr"
            minHeight="90px"
          />
          <TranslationField
            label="ترجمة نص السؤال"
            value={form.translation}
            onChange={(v) => setForm((prev) => ({ ...prev, translation: v }))}
          />
        </div>

        {/* Question Image */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            صورة السؤال (اختياري)
          </label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full max-h-48 object-contain rounded-xl border border-gray-200"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl py-6 cursor-pointer hover:border-amber-400 transition-colors">
              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">اضغط لرفع صورة</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Options — each is now a mini rich text editor */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 text-right">
            الخيارات
          </label>
          {form.options.map((opt, i) => (
            <div key={i} className="flex items-start gap-2">
              <input
                type="radio"
                name="correct"
                checked={form.correctIndex === i}
                onChange={() =>
                  !isHtmlEmpty(opt) && setForm({ ...form, correctIndex: i })
                }
                className="accent-amber-600 mt-3"
              />
              <div className="flex-1 relative">
                <span className="absolute left-3 top-2 text-sm font-bold text-gray-400 z-10">
                  {String.fromCharCode(65 + i)}
                </span>
                <div
                  className={`pl-8 rounded-xl ${
                    form.correctIndex === i && !isHtmlEmpty(opt)
                      ? "ring-2 ring-amber-400"
                      : ""
                  }`}
                >
                  <RichTextEditor
                    key={`opt-${i}-${formKey}`}
                    value={opt}
                    onChange={(html) => updateOption(i, html)}
                    dir="ltr"
                    minHeight="44px"
                    compact
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Explanation (Arabic) */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            الشرح بالعربي (اختياري)
          </label>
          <RichTextEditor
            key={`exp-ar-${formKey}`}
            value={form.explanation}
            onChange={(html) => setForm({ ...form, explanation: html })}
            dir="rtl"
            minHeight="60px"
          />
        </div>

        {/* Explanation (English) */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-left">
            Explanation in English (optional)
          </label>
          <RichTextEditor
            key={`exp-en-${formKey}`}
            value={form.english_explanation}
            onChange={(html) =>
              setForm({ ...form, english_explanation: html })
            }
            dir="ltr"
            minHeight="60px"
          />
        </div>

        {/* Difficulty & Points */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              الصعوبة
            </label>
            <select
              value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-right bg-white"
            >
              <option value="EASY">سهل</option>
              <option value="MEDIUM">متوسط</option>
              <option value="HARD">صعب</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              النقاط
            </label>
            <input
              type="number"
              value={form.points}
              onChange={(e) =>
                setForm({ ...form, points: parseInt(e.target.value) || 1 })
              }
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-right"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/esp/skills/${skillId}`)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
          >
            رجوع
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            حفظ السؤال
          </button>
        </div>
      </form>
    </div>
  );
}